/**
 * React Query hook — conversation detail with local cache + background refresh.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getConversationByPhone } from "../services/conversationListService.js";
import {
  fingerprintConversationDetail,
  getConversationDetailCache,
  setConversationDetailCache,
} from "../utils/conversationCache.js";
import {
  SYNC_VARIANT,
  deriveConversationSyncStatus,
} from "../utils/conversationSyncStatus.js";

export function conversationDetailQueryKey(phone, leadId = null) {
  return ["conversation-detail", phone, leadId ?? null];
}

export function useConversationDetail(phone, { leadId = null, enabled = true } = {}) {
  const cacheEntry = useMemo(
    () => (phone ? getConversationDetailCache(phone, leadId) : null),
    [phone, leadId]
  );
  const hadCachedData = Boolean(cacheEntry?.data);
  const [transientNotice, setTransientNotice] = useState(null);
  const previousFingerprintRef = useRef(
    cacheEntry?.data ? fingerprintConversationDetail(cacheEntry.data) : null
  );
  const completedFetchRef = useRef(false);

  const query = useQuery({
    queryKey: conversationDetailQueryKey(phone, leadId),
    queryFn: async () => {
      const result = await getConversationByPhone(phone, {
        leadId: leadId || undefined,
      });
      setConversationDetailCache(phone, leadId, result);
      return result;
    },
    enabled: Boolean(phone) && enabled,
    staleTime: 60_000,
    initialData: cacheEntry?.data,
    initialDataUpdatedAt: cacheEntry?.fetchedAt,
    retry: 1,
  });

  useEffect(() => {
    completedFetchRef.current = false;
    previousFingerprintRef.current = cacheEntry?.data
      ? fingerprintConversationDetail(cacheEntry.data)
      : null;
    setTransientNotice(null);
  }, [phone, leadId, cacheEntry?.data]);

  useEffect(() => {
    if (!query.isSuccess || query.isFetching || !query.data) return;

    const fingerprint = fingerprintConversationDetail(query.data);
    const previous = previousFingerprintRef.current;

    if (completedFetchRef.current) {
      if (previous && previous !== fingerprint) {
        setTransientNotice(SYNC_VARIANT.UPDATED);
      } else {
        setTransientNotice(SYNC_VARIANT.UP_TO_DATE);
      }
    }

    previousFingerprintRef.current = fingerprint;
    completedFetchRef.current = true;
  }, [query.isSuccess, query.isFetching, query.data, query.dataUpdatedAt]);

  useEffect(() => {
    if (!transientNotice) return undefined;
    const timer = window.setTimeout(() => setTransientNotice(null), 3000);
    return () => window.clearTimeout(timer);
  }, [transientNotice]);

  const syncStatus = deriveConversationSyncStatus({
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    hadCachedData,
    transientNotice,
  });

  return {
    ...query,
    hadCachedData,
    cachedAt: cacheEntry?.fetchedAt ?? null,
    syncStatus,
    refresh: query.refetch,
  };
}
